import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DataTable } from "../../components/layout/DataTable";
import { EmptyState } from "../../components/layout/EmptyState";
import { MetricCard } from "../../components/layout/MetricCard";
import { Panel } from "../../components/layout/Panel";
import { WarningPanel } from "../../components/layout/WarningPanel";
import { SectionIntro } from "../../components/layout/SectionIntro";
import { apiFetch } from "../../lib/api";
import { toLocalIsoDate } from "../../lib/date";
import {
  formatCapitalReason,
  formatCapitalSignalCoverage,
  formatDate,
  formatDateTime,
  formatMoney,
  formatPct,
  sumTruthStageUnits,
} from "../../lib/format";
import {
  clearPendingIdempotencyKey,
  createIdempotencyKey,
  getOrCreatePendingIdempotencyKey,
  type PendingIdempotencyEntry,
} from "../../lib/idempotency";
import { parseNumericDraft } from "../../lib/numericDraft";
import { readNullableStringParam } from "../../lib/url-state";
import type {
  CapitalPlanDetailResponse,
  CapitalPlanListResponse,
  CapitalPlanRunSummary,
  CapitalPlanWave,
  DashboardResponse,
  PurchaseOrder,
  PurchaseOrderListResponse,
  PurchaseOrderTimelineEvent,
  PurchaseOrderTimelineResponse,
  RecommendationRow,
  SupplierFeedbackListResponse,
  SupplierFeedbackRow,
  SupplierFreightRule,
  SupplierFreightRuleListResponse,
  SupplierOrderProposal,
  SupplierOrderProposalListResponse,
  SupplierProfile,
  SupplierTerm,
} from "../../lib/types";
import { useDomainVersion, useInvalidation } from "../../app/invalidation";

type ProcurementWorkspaceState = {
  loading: boolean;
  error: string | null;
  purchaseOrderWarning: string | null;
  capitalPlanBuildWarnings: string[];
  currentCashDraft: string;
  currentCashValid: boolean;
  scenario: string;
  requestedRunId: string | null;
  activeRunId: string | null;
  isRunContextLoading: boolean;
  selectedProposalId: string | null;
  selectedCapitalPlanId: string | null;
  selectedCapitalWaveId: string | null;
  selectedPoId: string | null;
  supplierProposals: SupplierOrderProposal[];
  purchaseOrders: PurchaseOrder[];
  proposalSupplierTerms: SupplierTerm | null;
  supplierProfile: SupplierProfile | null;
  supplierFreightRules: SupplierFreightRule[];
  supplierFeedback: SupplierFeedbackRow[];
  groupedProposals: Record<string, SupplierOrderProposal[]>;
  proposalWarnings: string[];
  proposalOmittedRows: Array<{ sku: string; tier: string; rawQty: number; reason: string }>;
  capitalPlans: CapitalPlanRunSummary[];
  capitalPlan: CapitalPlanDetailResponse | null;
  selectedCapitalWave: CapitalPlanWave | null;
  selectedProposal: SupplierOrderProposal | null;
  selectedPo: PurchaseOrder | null;
  selectedPoTimeline: PurchaseOrderTimelineEvent[];
  relatedProposalPo: PurchaseOrder | null;
  rows: RecommendationRow[];
  handleRunCapitalPlan: () => Promise<void>;
  handleProposalAction: (path: string) => Promise<void>;
  handleCreatePo: (event: FormEvent) => Promise<void>;
  handlePoAction: (path: string, body?: Record<string, unknown>, headers?: Record<string, string>) => Promise<boolean>;
  handleAutoShip: () => Promise<void>;
  handleAutoReceive: () => Promise<void>;
  poSupplier: string;
  setPoSupplier: (value: string) => void;
  poSku: string;
  setPoSku: (value: string) => void;
  poUnitsDraft: string;
  setPoUnitsDraft: (value: string) => void;
  poUnitCostDraft: string;
  setPoUnitCostDraft: (value: string) => void;
  canCreatePo: boolean;
  poReceiptDate: string;
  setPoReceiptDate: (value: string) => void;
  setCurrentCashDraft: (value: string) => void;
  setScenario: (value: string) => void;
  updateSelection: (updates: Record<string, string | null>) => void;
};

type ProcurementDashboardSnapshot = {
  runId: string | null;
  rows: RecommendationRow[];
};

type SupplierContextSnapshot = {
  freightRules: SupplierFreightRule[];
  profile: SupplierProfile | null;
  supplierTerms: SupplierTerm | null;
};

type PendingBuiltCapitalPlan = {
  summary: CapitalPlanRunSummary;
  preferredWaveId: string | null;
};

type PendingPoMutation = {
  key: string;
  receiptDate?: string;
  shipDate?: string;
  shipmentRef?: string;
};

type SearchValue = string | number | boolean | null | undefined;

const PURCHASE_ORDER_CREATE_SCOPE = "purchase-order-create";
const PURCHASE_ORDER_REFRESH_WARNING =
  "Purchase order was created, but the purchase order list could not be refreshed. Retrying the same submission is safe.";
const CAPITAL_PLAN_RUN_SCOPE = "capital-plan-run";
const CAPITAL_PLAN_BUILD_WARNING_MESSAGE =
  "Capital plan was created, but the follow-up procurement refresh failed. Retrying the same action is safe.";

function applySearchParamUpdates(searchParams: URLSearchParams, updates: Record<string, SearchValue>) {
  const next = new URLSearchParams(searchParams);
  Object.entries(updates).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "" || value === false) {
      next.delete(key);
      return;
    }
    next.set(key, String(value));
  });
  return next;
}

function normalizeSearchParamValue(value: SearchValue): string | null {
  if (value === null || value === undefined || value === "" || value === false) {
    return null;
  }
  return String(value);
}

function normalizeSelectionId<T extends { id: string }>(items: T[], preferredId: string | null | undefined): string | null {
  return preferredId && items.some((item) => item.id === preferredId) ? preferredId : items[0]?.id || null;
}

function normalizePurchaseOrderId(items: PurchaseOrder[], preferredId: string | null | undefined): string | null {
  if (preferredId && items.some((item) => item.id === preferredId)) {
    return preferredId;
  }
  const ranked = [...items].sort((a, b) => {
    const rank = (po: PurchaseOrder) => {
      switch (po.status) {
        case "shipped":
          return 0;
        case "approved":
        case "planned":
          return 1;
        case "placed":
        case "deposit_paid":
        case "in_production":
          return 2;
        case "received":
          return 4;
        default:
          return 3;
      }
    };
    const diff = rank(a) - rank(b);
    if (diff !== 0) {
      return diff;
    }
    return String(b.expected_arrival_date || "").localeCompare(String(a.expected_arrival_date || ""));
  });
  return ranked[0]?.id || null;
}

function buildCapitalPlanRunSummary(payload: CapitalPlanDetailResponse): CapitalPlanRunSummary {
  const {
    weeks: _weeks,
    funded_waves: _fundedWaves,
    deferred_waves: _deferredWaves,
    reserved_waves: _reservedWaves,
    ...summary
  } = payload;
  return summary;
}

function buildRunContextRequestKey(runId: string | null, procurementVersion: number) {
  return JSON.stringify({
    procurementVersion,
    runId: runId ?? "__latest__",
  });
}

function normalizeProposalId(proposals: SupplierOrderProposal[], preferredProposalId: string | null) {
  if (preferredProposalId && proposals.some((proposal) => proposal.id === preferredProposalId)) {
    return preferredProposalId;
  }
  const ranked = [...proposals].sort((a, b) => {
    const statusRank = (proposal: SupplierOrderProposal) => {
      switch (proposal.proposal_status) {
        case "proposed":
          return 0;
        case "approved":
          return 1;
        case "converted":
          return 2;
        case "rejected":
          return 3;
        default:
          return 4;
      }
    };
    const statusDiff = statusRank(a) - statusRank(b);
    if (statusDiff !== 0) {
      return statusDiff;
    }
    return Number(b.protected_profit_value || 0) - Number(a.protected_profit_value || 0);
  });
  return ranked[0]?.id || null;
}

function useProcurementWorkspace(): ProcurementWorkspaceState {
  const procurementVersion = useDomainVersion("procurement");
  const { markDirty } = useInvalidation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [purchaseOrderWarning, setPurchaseOrderWarning] = useState<string | null>(null);
  const [capitalPlanBuildWarnings, setCapitalPlanBuildWarnings] = useState<string[]>([]);
  const [currentCashDraft, setCurrentCashDraft] = useState("50000");
  const [scenario, setScenario] = useState("base");
  const [rows, setRows] = useState<RecommendationRow[]>([]);
  const [supplierProposals, setSupplierProposals] = useState<SupplierOrderProposal[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [selectedPoTimeline, setSelectedPoTimeline] = useState<PurchaseOrderTimelineEvent[]>([]);
  const [capitalPlans, setCapitalPlans] = useState<CapitalPlanRunSummary[]>([]);
  const [capitalPlan, setCapitalPlan] = useState<CapitalPlanDetailResponse | null>(null);
  const [proposalSupplierTerms, setProposalSupplierTerms] = useState<SupplierTerm | null>(null);
  const [supplierProfile, setSupplierProfile] = useState<SupplierProfile | null>(null);
  const [supplierFreightRules, setSupplierFreightRules] = useState<SupplierFreightRule[]>([]);
  const [supplierFeedback, setSupplierFeedback] = useState<SupplierFeedbackRow[]>([]);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [activeProposalId, setActiveProposalId] = useState<string | null>(null);
  const [isRunContextLoading, setIsRunContextLoading] = useState(false);
  const [poSupplier, setPoSupplier] = useState("");
  const [poSku, setPoSku] = useState("");
  const [poUnitsDraft, setPoUnitsDraft] = useState("");
  const [poUnitCostDraft, setPoUnitCostDraft] = useState("");
  const [poReceiptDate, setPoReceiptDate] = useState("");
  const parsedCurrentCash = parseNumericDraft(currentCashDraft, "decimal");
  const currentCash = parsedCurrentCash.valid ? parsedCurrentCash.parsed : null;
  const currentCashValid = currentCash !== null;
  const parsedPoUnits = parseNumericDraft(poUnitsDraft, "integer");
  const poUnits = parsedPoUnits.valid ? parsedPoUnits.parsed : null;
  const poUnitsValid = poUnits !== null && poUnits > 0;
  const parsedPoUnitCost = parseNumericDraft(poUnitCostDraft, "decimal");
  const poUnitCost = parsedPoUnitCost.valid ? parsedPoUnitCost.parsed : null;
  const poUnitCostValid = poUnitCost !== null && poUnitCost > 0;
  const canCreatePo = Boolean(poSupplier && poSku && poReceiptDate) && poUnitsValid && poUnitCostValid;
  const pendingPoMutationsRef = useRef<Record<string, PendingPoMutation>>({});
  const pendingMutationKeysRef = useRef<Record<string, PendingIdempotencyEntry>>({});
  const requestedRunId = readNullableStringParam(searchParams, "runId");
  const requestedProposalId = readNullableStringParam(searchParams, "proposalId");
  const selectedCapitalPlanId = readNullableStringParam(searchParams, "capitalPlanId");
  const selectedCapitalWaveId = readNullableStringParam(searchParams, "capitalWaveId");
  const selectedPoId = readNullableStringParam(searchParams, "poId");
  const requestedRunIdRef = useRef<string | null>(requestedRunId);
  const requestedProposalIdRef = useRef<string | null>(requestedProposalId);
  const selectedCapitalPlanIdRef = useRef<string | null>(selectedCapitalPlanId);
  const selectedCapitalWaveIdRef = useRef<string | null>(selectedCapitalWaveId);
  const selectedPoIdRef = useRef<string | null>(selectedPoId);
  const activeRunIdRef = useRef<string | null>(activeRunId);
  const activeProposalIdRef = useRef<string | null>(activeProposalId);
  const runContextRequestSeqRef = useRef(0);
  const activeRunContextKeyRef = useRef<string | null>(null);
  const suppressedRunContextKeyRef = useRef<string | null>(null);
  const capitalPlansRequestSeqRef = useRef(0);
  const capitalPlanDetailRequestSeqRef = useRef(0);
  const suppressNextCapitalPlanDetailRef = useRef<string | null>(null);
  const pendingBuiltCapitalPlanRef = useRef<PendingBuiltCapitalPlan | null>(null);
  const purchaseOrdersRequestSeqRef = useRef(0);
  const pendingCreatedPoIdRef = useRef<string | null>(null);
  const supplierFeedbackRequestSeqRef = useRef(0);
  const supplierContextRequestSeqRef = useRef(0);
  const supplierContextSupplierRef = useRef<string | null>(null);

  useEffect(() => {
    requestedRunIdRef.current = requestedRunId;
  }, [requestedRunId]);

  useEffect(() => {
    requestedProposalIdRef.current = requestedProposalId;
  }, [requestedProposalId]);

  useEffect(() => {
    selectedCapitalPlanIdRef.current = selectedCapitalPlanId;
    const pendingBuiltCapitalPlan = pendingBuiltCapitalPlanRef.current;
    if (
      pendingBuiltCapitalPlan &&
      (!selectedCapitalPlanId || selectedCapitalPlanId !== pendingBuiltCapitalPlan.summary.id)
    ) {
      pendingBuiltCapitalPlanRef.current = null;
    }
  }, [selectedCapitalPlanId]);

  useEffect(() => {
    selectedCapitalWaveIdRef.current = selectedCapitalWaveId;
  }, [selectedCapitalWaveId]);

  useEffect(() => {
    selectedPoIdRef.current = selectedPoId;
  }, [selectedPoId]);

  useEffect(() => {
    activeRunIdRef.current = activeRunId;
  }, [activeRunId]);

  useEffect(() => {
    activeProposalIdRef.current = activeProposalId;
  }, [activeProposalId]);

  const selectedProposal = useMemo(
    () => supplierProposals.find((proposal) => proposal.id === activeProposalId) || null,
    [activeProposalId, supplierProposals],
  );
  const selectedPo = useMemo(
    () => purchaseOrders.find((po) => po.id === selectedPoId) || null,
    [purchaseOrders, selectedPoId],
  );
  const relatedProposalPo = useMemo(
    () =>
      selectedProposal ? purchaseOrders.find((po) => po.supplier_order_proposal_id === selectedProposal.id) || null : null,
    [purchaseOrders, selectedProposal],
  );

  function getPendingPoMutation(poId: string, action: "auto-ship" | "auto-receive") {
    const storageKey = `${poId}:${action}`;
    const existing = pendingPoMutationsRef.current[storageKey];
    if (existing) return existing;
    const next: PendingPoMutation = { key: createIdempotencyKey() };
    pendingPoMutationsRef.current[storageKey] = next;
    return next;
  }

  function clearPendingPoMutation(poId: string, action: "auto-ship" | "auto-receive") {
    delete pendingPoMutationsRef.current[`${poId}:${action}`];
  }

  function buildCurrentSearchParams() {
    const next = new URLSearchParams();
    if (requestedRunIdRef.current) {
      next.set("runId", requestedRunIdRef.current);
    }
    if (requestedProposalIdRef.current) {
      next.set("proposalId", requestedProposalIdRef.current);
    }
    if (selectedCapitalPlanIdRef.current) {
      next.set("capitalPlanId", selectedCapitalPlanIdRef.current);
    }
    if (selectedCapitalWaveIdRef.current) {
      next.set("capitalWaveId", selectedCapitalWaveIdRef.current);
    }
    if (selectedPoIdRef.current) {
      next.set("poId", selectedPoIdRef.current);
    }
    return next;
  }

  function patchCurrentSearchParams(updates: Record<string, SearchValue>) {
    if ("runId" in updates) {
      requestedRunIdRef.current = normalizeSearchParamValue(updates.runId);
    }
    if ("proposalId" in updates) {
      requestedProposalIdRef.current = normalizeSearchParamValue(updates.proposalId);
    }
    if ("capitalPlanId" in updates) {
      selectedCapitalPlanIdRef.current = normalizeSearchParamValue(updates.capitalPlanId);
    }
    if ("capitalWaveId" in updates) {
      selectedCapitalWaveIdRef.current = normalizeSearchParamValue(updates.capitalWaveId);
    }
    if ("poId" in updates) {
      selectedPoIdRef.current = normalizeSearchParamValue(updates.poId);
    }
    const next = applySearchParamUpdates(buildCurrentSearchParams(), updates);
    setSearchParams(next, { replace: true });
  }

  const selectedCapitalWave = useMemo(() => {
    const waves = [
      ...(capitalPlan?.funded_waves || []),
      ...(capitalPlan?.deferred_waves || []),
      ...(capitalPlan?.reserved_waves || []),
    ];
    return waves.find((wave) => wave.id === selectedCapitalWaveId) || waves[0] || null;
  }, [capitalPlan, selectedCapitalWaveId]);

  const groupedProposals = useMemo(() => {
    const groups: Record<string, SupplierOrderProposal[]> = {
      proposed: [],
      approved: [],
      converted: [],
      rejected: [],
    };
    for (const proposal of supplierProposals) {
      if (proposal.proposal_status in groups) {
        groups[proposal.proposal_status].push(proposal);
      }
    }
    return groups;
  }, [supplierProposals]);

  const proposalWarnings = useMemo(() => {
    const warnings: string[] = [];
    if (!selectedProposal) return warnings;
    const minOrderFillPct = selectedProposal.explainability?.min_order_fill_pct;
    if (selectedProposal.cash_feasibility_status !== "feasible") {
      warnings.push(`Proposal cash feasibility is ${selectedProposal.cash_feasibility_status}.`);
    }
    if (!selectedProposal.on_cycle) {
      warnings.push("This proposal is off-cycle and bypasses the supplier cadence anchor.");
    }
    if (minOrderFillPct !== null && minOrderFillPct !== undefined && supplierProfile?.min_order_value) {
      warnings.push(
        `Supplier minimum fill is ${Number(minOrderFillPct).toFixed(1)}% of ${formatMoney(
          supplierProfile.min_order_value,
        )}.`,
      );
    }
    return warnings;
  }, [selectedProposal, supplierProfile]);

  const proposalOmittedRows = useMemo(() => {
    if (!selectedProposal) return [];
    const rejectedLines = selectedProposal.explainability?.rejected_lines ?? [];
    if (rejectedLines.length > 0) {
      return rejectedLines.map((item) => {
        const row = rows.find((candidate) => candidate.sku === item.sku);
        return {
          sku: item.sku || "-",
          tier: row?.tier || "-",
          rawQty: row?.raw_recommended_qty ?? row?.recommended_qty ?? 0,
          reason: item.reason || row?.defer_reason || row?.supplier_batch_status || "not_selected",
        };
      });
    }
    return rows
      .filter(
        (row) =>
          row.supplier === selectedProposal.supplier &&
          !row.blocked &&
          (row.raw_recommended_qty || 0) > 0 &&
          row.supplier_order_proposal_id !== selectedProposal.id,
      )
      .map((row) => ({
        sku: row.sku,
        tier: row.tier,
        rawQty: row.raw_recommended_qty ?? row.recommended_qty,
        reason: row.defer_reason || row.supplier_batch_status || "not_selected",
      }));
  }, [rows, selectedProposal]);

  function updateSelection(updates: Record<string, string | null>) {
    if (Object.prototype.hasOwnProperty.call(updates, "capitalPlanId")) {
      const nextCapitalPlanId = normalizeSearchParamValue(updates.capitalPlanId);
      const pendingBuiltCapitalPlan = pendingBuiltCapitalPlanRef.current;
      if (
        pendingBuiltCapitalPlan &&
        (!nextCapitalPlanId || nextCapitalPlanId !== pendingBuiltCapitalPlan.summary.id)
      ) {
        pendingBuiltCapitalPlanRef.current = null;
      }
    }
    patchCurrentSearchParams(updates);
  }

  function applySupplierContextSnapshot(nextSupplier: string | null, snapshot: SupplierContextSnapshot) {
    setProposalSupplierTerms(snapshot.supplierTerms);
    setSupplierProfile(snapshot.profile);
    setSupplierFreightRules(snapshot.freightRules);
    supplierContextSupplierRef.current = nextSupplier;
  }

  async function fetchDashboard(nextRequestedRunId?: string | null): Promise<ProcurementDashboardSnapshot> {
    const query = new URLSearchParams();
    if (nextRequestedRunId) query.set("run_id", nextRequestedRunId);
    const data = await apiFetch<DashboardResponse>(`/dashboard/replenishment?${query.toString()}`);
    return {
      runId: data.run_id ?? null,
      rows: data.rows || [],
    };
  }

  async function fetchSupplierProposals(nextRunId: string) {
    const payload = await apiFetch<SupplierOrderProposalListResponse>(
      `/runs/recommendations/${nextRunId}/supplier-orders`,
    );
    return payload.items || [];
  }

  async function fetchSupplierContext(nextSupplier: string | null | undefined): Promise<SupplierContextSnapshot> {
    if (!nextSupplier) {
      return {
        freightRules: [],
        profile: null,
        supplierTerms: null,
      };
    }

    const [termResult, profileResult, freightResult] = await Promise.allSettled([
      apiFetch<SupplierTerm>(`/suppliers/${encodeURIComponent(nextSupplier)}/terms`),
      apiFetch<SupplierProfile>(`/suppliers/${encodeURIComponent(nextSupplier)}/profile`),
      apiFetch<SupplierFreightRuleListResponse>(`/suppliers/${encodeURIComponent(nextSupplier)}/freight-rules`),
    ]);

    return {
      supplierTerms: termResult.status === "fulfilled" ? termResult.value : null,
      profile: profileResult.status === "fulfilled" ? profileResult.value : null,
      freightRules: freightResult.status === "fulfilled" ? freightResult.value.items || [] : [],
    };
  }

  async function loadCapitalPlans() {
    const requestId = ++capitalPlansRequestSeqRef.current;
    try {
      const payload = await apiFetch<CapitalPlanListResponse>("/capital-planning?limit=25");
      if (requestId !== capitalPlansRequestSeqRef.current) {
        return;
      }

      const items = payload.items || [];
      const pendingBuiltCapitalPlan = pendingBuiltCapitalPlanRef.current;
      let mergedItems = items;
      let preferredPlanId = selectedCapitalPlanIdRef.current;

      if (pendingBuiltCapitalPlan) {
        const hasServerBackedPendingPlan = items.some((item) => item.id === pendingBuiltCapitalPlan.summary.id);
        if (hasServerBackedPendingPlan) {
          pendingBuiltCapitalPlanRef.current = null;
        } else {
          mergedItems = [
            pendingBuiltCapitalPlan.summary,
            ...items.filter((item) => item.id !== pendingBuiltCapitalPlan.summary.id),
          ];
          preferredPlanId = pendingBuiltCapitalPlan.summary.id;
        }
      }

      const nextId = normalizeSelectionId(mergedItems, preferredPlanId);
      setCapitalPlans(mergedItems);
      if (nextId) {
        const nextUpdates: Record<string, SearchValue> = { capitalPlanId: nextId };
        if (
          pendingBuiltCapitalPlanRef.current &&
          nextId === pendingBuiltCapitalPlanRef.current.summary.id &&
          pendingBuiltCapitalPlanRef.current.preferredWaveId &&
          selectedCapitalWaveIdRef.current !== pendingBuiltCapitalPlanRef.current.preferredWaveId
        ) {
          nextUpdates.capitalWaveId = pendingBuiltCapitalPlanRef.current.preferredWaveId;
        }
        patchCurrentSearchParams(nextUpdates);
      } else {
        patchCurrentSearchParams({ capitalPlanId: null, capitalWaveId: null });
      }
    } catch (err) {
      if (requestId !== capitalPlansRequestSeqRef.current) {
        return;
      }
      throw err;
    }
  }

  async function loadCapitalPlanDetail(planRunId: string) {
    const requestId = ++capitalPlanDetailRequestSeqRef.current;
    try {
      const payload = await apiFetch<CapitalPlanDetailResponse>(`/capital-planning/${planRunId}`);
      if (requestId !== capitalPlanDetailRequestSeqRef.current) {
        return;
      }

      const waves = [...payload.funded_waves, ...payload.deferred_waves, ...payload.reserved_waves];
      const nextWaveId = normalizeSelectionId(waves, selectedCapitalWaveIdRef.current);
      setCapitalPlan(payload);
      patchCurrentSearchParams({ capitalWaveId: nextWaveId });
    } catch (err) {
      if (requestId !== capitalPlanDetailRequestSeqRef.current) {
        return;
      }
      throw err;
    }
  }

  async function loadPurchaseOrders(
    preferredPoId?: string | null,
    options: { resolvePendingCreate?: boolean } = {},
  ) {
    if (preferredPoId !== undefined) {
      selectedPoIdRef.current = preferredPoId;
    }

    const requestId = ++purchaseOrdersRequestSeqRef.current;
    try {
      const payload = await apiFetch<PurchaseOrderListResponse>("/purchase-orders");
      if (requestId !== purchaseOrdersRequestSeqRef.current) {
        return;
      }

      const items = payload.items || [];
      const nextId = normalizePurchaseOrderId(items, selectedPoIdRef.current);
      setPurchaseOrders(items);
      patchCurrentSearchParams({ poId: nextId });
      if (
        options.resolvePendingCreate &&
        pendingCreatedPoIdRef.current &&
        items.some((po) => po.id === pendingCreatedPoIdRef.current)
      ) {
        pendingCreatedPoIdRef.current = null;
        clearPendingIdempotencyKey(pendingMutationKeysRef.current, PURCHASE_ORDER_CREATE_SCOPE);
        setPurchaseOrderWarning(null);
      }
    } catch (err) {
      if (requestId !== purchaseOrdersRequestSeqRef.current) {
        return;
      }
      throw err;
    }
  }

  async function loadPurchaseOrderTimeline(poId: string) {
    const payload = await apiFetch<PurchaseOrderTimelineResponse>(`/purchase-orders/${poId}/timeline`);
    setSelectedPoTimeline(payload.events || []);
  }

  async function loadSupplierFeedback() {
    const requestId = ++supplierFeedbackRequestSeqRef.current;
    try {
      const payload = await apiFetch<SupplierFeedbackListResponse>("/feedback/suppliers?window_days=90");
      if (requestId !== supplierFeedbackRequestSeqRef.current) {
        return;
      }
      setSupplierFeedback(payload.items || []);
    } catch (err) {
      if (requestId !== supplierFeedbackRequestSeqRef.current) {
        return;
      }
      throw err;
    }
  }

  async function refreshIndependentData() {
    await Promise.all([loadCapitalPlans(), loadPurchaseOrders(), loadSupplierFeedback()]);
  }

  async function refreshRunContext(
    nextRequestedRunId?: string | null,
    options: { force?: boolean; requestedProposalId?: string | null } = {},
  ) {
    const requestKey = buildRunContextRequestKey(nextRequestedRunId ?? null, procurementVersion);

    if (suppressedRunContextKeyRef.current === requestKey) {
      suppressedRunContextKeyRef.current = null;
      return;
    }

    if (!options.force && activeRunContextKeyRef.current === requestKey) {
      return;
    }

    const requestId = ++runContextRequestSeqRef.current;
    const shouldShowRunContextLoading = activeRunIdRef.current !== null && nextRequestedRunId !== activeRunIdRef.current;
    setError(null);
    if (shouldShowRunContextLoading) {
      setIsRunContextLoading(true);
    }

    try {
      const dashboard = await fetchDashboard(nextRequestedRunId);
      if (requestId !== runContextRequestSeqRef.current) {
        return;
      }

      const nextSupplierProposals = dashboard.runId ? await fetchSupplierProposals(dashboard.runId) : [];
      if (requestId !== runContextRequestSeqRef.current) {
        return;
      }

      const preferredProposalId = options.requestedProposalId ?? requestedProposalIdRef.current;
      const nextProposalId = normalizeProposalId(nextSupplierProposals, preferredProposalId);
      const nextSelectedProposal =
        nextSupplierProposals.find((proposal) => proposal.id === nextProposalId) || null;
      const nextSupplierContext = await fetchSupplierContext(nextSelectedProposal?.supplier);
      if (requestId !== runContextRequestSeqRef.current) {
        return;
      }

      setRows(dashboard.rows);
      setSupplierProposals(nextSupplierProposals);
      setActiveRunId(dashboard.runId);
      setActiveProposalId(nextProposalId);
      applySupplierContextSnapshot(nextSelectedProposal?.supplier || null, nextSupplierContext);
      activeRunContextKeyRef.current = buildRunContextRequestKey(dashboard.runId, procurementVersion);
      patchCurrentSearchParams({
        runId: dashboard.runId,
        proposalId: nextProposalId,
      });
    } catch (err) {
      if (requestId !== runContextRequestSeqRef.current) {
        return;
      }
      if (activeRunIdRef.current === null && nextRequestedRunId !== null) {
        suppressedRunContextKeyRef.current = buildRunContextRequestKey(null, procurementVersion);
      }
      patchCurrentSearchParams({
        runId: activeRunIdRef.current,
        proposalId: activeProposalIdRef.current,
      });
      throw err;
    } finally {
      if (requestId === runContextRequestSeqRef.current) {
        setIsRunContextLoading(false);
      }
    }
  }

  async function handleRunCapitalPlan() {
    if (currentCash === null) {
      return;
    }
    setLoading(true);
    setError(null);
    setCapitalPlanBuildWarnings([]);
    const requestBody = JSON.stringify({
      current_cash_on_hand: currentCash,
      planning_horizon_weeks: 6,
      scenario,
      reserve_open_pos: true,
      reserve_approved_pos: true,
      reserve_approved_supplier_proposals: true,
    });
    const key = getOrCreatePendingIdempotencyKey(
      pendingMutationKeysRef.current,
      CAPITAL_PLAN_RUN_SCOPE,
      requestBody,
    );
    let capitalPlanCreated = false;
    try {
      const data = await apiFetch<CapitalPlanDetailResponse>("/capital-planning/run", {
        method: "POST",
        body: requestBody,
        headers: { "Idempotency-Key": key },
      });
      capitalPlanCreated = true;
      const waves = [...data.funded_waves, ...data.deferred_waves, ...data.reserved_waves];
      const linkedProposalId =
        data.funded_waves.find((wave) => wave.supplier_order_proposal_id)?.supplier_order_proposal_id ||
        activeProposalIdRef.current;
      const preferredWaveId = waves[0]?.id || null;

      setCapitalPlan(data);
      suppressNextCapitalPlanDetailRef.current = data.id;
      pendingBuiltCapitalPlanRef.current = {
        summary: buildCapitalPlanRunSummary(data),
        preferredWaveId,
      };
      patchCurrentSearchParams({
        runId: data.source_recommendation_run_id,
        capitalPlanId: data.id,
        capitalWaveId: preferredWaveId,
        proposalId: linkedProposalId,
      });
      await Promise.all([
        refreshIndependentData(),
        refreshRunContext(data.source_recommendation_run_id, {
          force: true,
          requestedProposalId: linkedProposalId,
        }),
      ]);
      clearPendingIdempotencyKey(pendingMutationKeysRef.current, CAPITAL_PLAN_RUN_SCOPE);
      markDirty(["planning", "procurement", "finance", "feedback"]);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (capitalPlanCreated) {
        setCapitalPlanBuildWarnings([CAPITAL_PLAN_BUILD_WARNING_MESSAGE]);
        setError(null);
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleProposalAction(path: string) {
    if (!selectedProposal) return;
    setLoading(true);
    setError(null);
    try {
      const result = await apiFetch<PurchaseOrder>(`/supplier-order-proposals/${selectedProposal.id}/${path}`, {
        method: "POST",
      });
      await Promise.all([
        loadPurchaseOrders(path === "convert-to-po" ? result.id : selectedPoIdRef.current),
        loadSupplierFeedback(),
        activeRunIdRef.current
          ? refreshRunContext(activeRunIdRef.current, {
              force: true,
              requestedProposalId: activeProposalIdRef.current,
            })
          : Promise.resolve(),
      ]);
      markDirty(["planning", "procurement", "finance", "feedback", "operations"]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreatePo(event: FormEvent) {
    event.preventDefault();
    if (!poSupplier || !poSku || !poReceiptDate || poUnits === null || poUnits <= 0 || poUnitCost === null || poUnitCost <= 0) {
      return;
    }
    setLoading(true);
    setError(null);
    setPurchaseOrderWarning(null);
    const requestBody = JSON.stringify({
      supplier: poSupplier,
      status: "approved",
      expected_arrival_date: poReceiptDate,
      lines: [
        {
          sku: poSku,
          units: poUnits,
          unit_cost: poUnitCost,
          expected_receipt_date: poReceiptDate,
          line_status: "planned",
        },
      ],
    });
    const existingCreateEntry = pendingMutationKeysRef.current[PURCHASE_ORDER_CREATE_SCOPE];
    if (existingCreateEntry && existingCreateEntry.body !== requestBody) {
      pendingCreatedPoIdRef.current = null;
    }
    const key = getOrCreatePendingIdempotencyKey(
      pendingMutationKeysRef.current,
      PURCHASE_ORDER_CREATE_SCOPE,
      requestBody,
    );
    try {
      const createdPo = await apiFetch<PurchaseOrder>("/purchase-orders", {
        method: "POST",
        body: requestBody,
        headers: { "Idempotency-Key": key },
      });
      setPoSupplier("");
      setPoSku("");
      setPoUnitsDraft("");
      setPoUnitCostDraft("");
      setPoReceiptDate("");
      pendingCreatedPoIdRef.current = createdPo.id;
      try {
        await loadPurchaseOrders(createdPo.id, { resolvePendingCreate: true });
      } catch {
        setPurchaseOrders((current) => [createdPo, ...current.filter((po) => po.id !== createdPo.id)]);
        setSelectedPoTimeline([]);
        patchCurrentSearchParams({ poId: createdPo.id });
        setPurchaseOrderWarning(PURCHASE_ORDER_REFRESH_WARNING);
      }
      markDirty(["procurement", "operations", "finance"]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function handlePoAction(
    path: string,
    body: Record<string, unknown> = {},
    headers?: Record<string, string>,
  ) {
    if (!selectedPo) return false;
    setLoading(true);
    setError(null);
    try {
      await apiFetch(`/purchase-orders/${selectedPo.id}/${path}`, {
        method: "POST",
        body: JSON.stringify(body),
        headers,
      });
      await Promise.all([loadPurchaseOrders(), loadPurchaseOrderTimeline(selectedPo.id), loadSupplierFeedback()]);
      markDirty(["procurement", "operations", "finance", "feedback"]);
      return true;
    } catch (err) {
      setError((err as Error).message);
      return false;
    } finally {
      setLoading(false);
    }
  }

  async function handleAutoShip() {
    if (!selectedPo) return;
    const pending = getPendingPoMutation(selectedPo.id, "auto-ship");
    const shipDate = pending.shipDate || toLocalIsoDate();
    const shipmentRef = pending.shipmentRef || `MANUAL-${Date.now()}`;
    pending.shipDate = shipDate;
    pending.shipmentRef = shipmentRef;
    const linePayload = selectedPo.lines
      .map((line) => ({
        po_line_id: line.id,
        units: Math.max((line.approved_qty_units || line.units) - (line.received_qty_units || 0), 0),
      }))
      .filter((line) => line.units > 0);
    if (!linePayload.length) return;
    const ok = await handlePoAction(
      "add-shipment",
      {
        shipment_ref: shipmentRef,
        ship_date: shipDate,
        expected_arrival_date: selectedPo.expected_arrival_date,
        status: "in_transit",
        lines: linePayload,
      },
      { "Idempotency-Key": pending.key },
    );
    if (ok) {
      clearPendingPoMutation(selectedPo.id, "auto-ship");
    }
  }

  async function handleAutoReceive() {
    if (!selectedPo) return;
    const pending = getPendingPoMutation(selectedPo.id, "auto-receive");
    const receiptDate = pending.receiptDate || toLocalIsoDate();
    pending.receiptDate = receiptDate;
    const linePayload = selectedPo.lines
      .map((line) => ({
        po_line_id: line.id,
        units_received: Math.max((line.approved_qty_units || line.units) - (line.received_qty_units || 0), 0),
      }))
      .filter((line) => line.units_received > 0);
    if (!linePayload.length) return;
    const ok = await handlePoAction(
      "receive",
      {
        receipt_date: receiptDate,
        lines: linePayload,
      },
      { "Idempotency-Key": pending.key },
    );
    if (ok) {
      clearPendingPoMutation(selectedPo.id, "auto-receive");
    }
  }

  useEffect(() => {
    refreshIndependentData().catch((err) => setError((err as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [procurementVersion]);

  useEffect(() => {
    refreshRunContext(requestedRunId).catch((err) => setError((err as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [procurementVersion, requestedRunId]);

  useEffect(() => {
    if (isRunContextLoading || requestedRunId !== activeRunId) {
      return;
    }
    const nextProposalId = normalizeProposalId(supplierProposals, requestedProposalId);
    if (nextProposalId !== activeProposalId) {
      setActiveProposalId(nextProposalId);
    }
    if (requestedProposalId !== nextProposalId) {
      patchCurrentSearchParams({ proposalId: nextProposalId });
    }
  }, [requestedProposalId, requestedRunId, activeRunId, supplierProposals, activeProposalId, isRunContextLoading]);

  useEffect(() => {
    if (selectedCapitalPlanId) {
      if (suppressNextCapitalPlanDetailRef.current === selectedCapitalPlanId) {
        suppressNextCapitalPlanDetailRef.current = null;
        return;
      }
      loadCapitalPlanDetail(selectedCapitalPlanId).catch((err) => setError((err as Error).message));
    } else {
      capitalPlanDetailRequestSeqRef.current += 1;
      setCapitalPlan(null);
      patchCurrentSearchParams({ capitalWaveId: null });
    }
  }, [selectedCapitalPlanId]);

  useEffect(() => {
    if (selectedProposal) {
      if (supplierContextSupplierRef.current === selectedProposal.supplier) {
        return;
      }

      const requestId = ++supplierContextRequestSeqRef.current;
      fetchSupplierContext(selectedProposal.supplier)
        .then((snapshot) => {
          if (requestId !== supplierContextRequestSeqRef.current) {
            return;
          }
          applySupplierContextSnapshot(selectedProposal.supplier, snapshot);
        })
        .catch((err) => {
          if (requestId !== supplierContextRequestSeqRef.current) {
            return;
          }
          setError((err as Error).message);
        });
    } else {
      applySupplierContextSnapshot(null, {
        freightRules: [],
        profile: null,
        supplierTerms: null,
      });
    }
  }, [selectedProposal?.supplier]);

  useEffect(() => {
    if (selectedPoId) {
      loadPurchaseOrderTimeline(selectedPoId).catch((err) => setError((err as Error).message));
    } else {
      setSelectedPoTimeline([]);
    }
  }, [selectedPoId]);

  useEffect(() => {
    pendingPoMutationsRef.current = {};
  }, [selectedPoId]);

  return {
    loading,
    error,
    purchaseOrderWarning,
    capitalPlanBuildWarnings,
    currentCashDraft,
    currentCashValid,
    scenario,
    requestedRunId,
    activeRunId,
    isRunContextLoading,
    selectedProposalId: activeProposalId,
    selectedCapitalPlanId,
    selectedCapitalWaveId,
    selectedPoId,
    supplierProposals,
    purchaseOrders,
    proposalSupplierTerms,
    supplierProfile,
    supplierFreightRules,
    supplierFeedback,
    groupedProposals,
    proposalWarnings,
    proposalOmittedRows,
    capitalPlans,
    capitalPlan,
    selectedCapitalWave,
    selectedProposal,
    selectedPo,
    selectedPoTimeline,
    relatedProposalPo,
    rows,
    handleRunCapitalPlan,
    handleProposalAction,
    handleCreatePo,
    handlePoAction,
    handleAutoShip,
    handleAutoReceive,
    poSupplier,
    setPoSupplier,
    poSku,
    setPoSku,
    poUnitsDraft,
    setPoUnitsDraft,
    poUnitCostDraft,
    setPoUnitCostDraft,
    canCreatePo,
    poReceiptDate,
    setPoReceiptDate,
    setCurrentCashDraft,
    setScenario,
    updateSelection,
  };
}

export function ProcurementWorkspace() {
  const navigate = useNavigate();
  const state = useProcurementWorkspace();
  const selectedSupplierFeedback = useMemo(() => {
    const supplier = state.selectedProposal?.supplier || state.selectedPo?.supplier || null;
    if (!supplier) return null;
    return state.supplierFeedback.find((row) => row.supplier === supplier) || null;
  }, [state.selectedPo?.supplier, state.selectedProposal?.supplier, state.supplierFeedback]);

  const capitalPlanSummary = state.capitalPlan
    ? (() => {
        const summary: string[] = [];
        summary.push(`${state.capitalPlan.summary.funded_wave_count} waves funded and ${state.capitalPlan.summary.deferred_wave_count} deferred.`);
        summary.push(`Immediate cash required is ${formatMoney(state.capitalPlan.summary.immediate_cash_required)} with ${formatMoney(state.capitalPlan.summary.lowest_ending_balance)} as the lowest projected ending balance.`);
        summary.push(`Protected profit totals ${formatMoney(state.capitalPlan.summary.protected_profit_value)}.`);
        if (Number(state.capitalPlan.summary.capital_efficiency.total_penalty_value || 0) > 0) {
          summary.push(`Capital efficiency penalties total ${formatMoney(state.capitalPlan.summary.capital_efficiency.total_penalty_value)} and should be reviewed before final approval.`);
        }
        return summary;
      })()
    : [];

  return (
    <>
      <SectionIntro
        eyebrow="Procurement"
        title="What should we approve and buy now?"
        description="Turn the best recommendations into simple approval decisions, supplier buys, and purchase orders without getting buried in planning detail."
      />
      <Panel>
        <h2>Buying plan</h2>
        <p className="muted-small">Most teams only need current cash, a scenario, and one refresh. Older plans and source-run links are available when you need them.</p>
        <div className="actions">
          <label>
            Current Cash
            <input
              type="number"
              step="0.01"
              value={state.currentCashDraft}
              onChange={(event) => state.setCurrentCashDraft(event.target.value)}
            />
          </label>
          <label>
            Scenario
            <select value={state.scenario} onChange={(event) => state.setScenario(event.target.value)}>
              <option value="base">Base</option>
              <option value="conservative_demand">Conservative Demand</option>
              <option value="delayed_inbound">Delayed Inbound</option>
            </select>
          </label>
          <button onClick={() => state.handleRunCapitalPlan()} disabled={state.loading || !state.currentCashValid}>
            Refresh Buying Plan
          </button>
        </div>
        <details className="workspace-details">
          <summary>Advanced buying options</summary>
          <div className="actions">
            <label>
              Plan
              <select value={state.selectedCapitalPlanId || ""} onChange={(event) => state.updateSelection({ capitalPlanId: event.target.value || null })}>
                <option value="">Latest</option>
                {state.capitalPlans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {new Date(plan.created_at).toLocaleString()} | {plan.scenario} | funded {plan.summary.funded_wave_count}
                  </option>
                ))}
              </select>
            </label>
            {state.capitalPlan && (
              <button type="button" onClick={() => navigate(`/planning?runId=${state.capitalPlan?.source_recommendation_run_id}`)}>
                Open Source Run
              </button>
            )}
          </div>
        </details>

        {state.capitalPlan ? (
          <>
            <div className="detail-cards">
              <MetricCard label="Source Run" value={state.capitalPlan.source_recommendation_run_id} />
              <MetricCard
                label="Funded / Deferred"
                value={`${state.capitalPlan.summary.funded_wave_count} / ${state.capitalPlan.summary.deferred_wave_count}`}
              />
              <MetricCard label="Reserved Waves" value={state.capitalPlan.summary.reserved_wave_count} />
              <MetricCard label="Protected Profit" value={formatMoney(state.capitalPlan.summary.protected_profit_value)} />
              <MetricCard label="Total Penalties" value={formatMoney(state.capitalPlan.summary.capital_efficiency.total_penalty_value)} />
              <MetricCard label="Immediate Cash" value={formatMoney(state.capitalPlan.summary.immediate_cash_required)} />
              <MetricCard label="Scheduled Cash" value={formatMoney(state.capitalPlan.summary.total_scheduled_cash)} />
              <MetricCard label="Lowest Ending Balance" value={formatMoney(state.capitalPlan.summary.lowest_ending_balance)} />
              <MetricCard
                label="Signal Coverage"
                value={formatCapitalSignalCoverage({
                  explicit: state.capitalPlan.summary.capital_efficiency.explicit_signal_count,
                  mixed: state.capitalPlan.summary.capital_efficiency.mixed_signal_count,
                  derived: state.capitalPlan.summary.capital_efficiency.derived_signal_count,
                })}
              />
            </div>

            <WarningPanel title="Things to review" warnings={[...(state.capitalPlan.warnings || []), ...state.capitalPlanBuildWarnings].slice(0, 6)} />

            {capitalPlanSummary.length > 0 && (
              <Panel>
                <h3>What to know right now</h3>
                <ul className="summary-list">
                  {capitalPlanSummary.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </Panel>
            )}

            <Panel className="workspace-control-panel recommended-action-panel">
              <h3>Recommended next step</h3>
              <p className="muted-small">Refresh the buying plan, then approve the next ready-to-approve supplier buy with the best cash and profit profile.</p>
              <div className="actions">
                <button className="button-primary-action" onClick={() => state.handleRunCapitalPlan()} disabled={state.loading || !state.currentCashValid}>
                  Refresh Buying Plan
                </button>
                {state.groupedProposals.proposed[0] && (
                  <button type="button" className="button-secondary" onClick={() => state.updateSelection({ proposalId: state.groupedProposals.proposed[0].id })}>
                    Open Next Buy Recommendation
                  </button>
                )}
              </div>
            </Panel>

            <h3>Weekly Cash Timeline</h3>
            <DataTable slim>
              <table>
                <thead>
                  <tr>
                    <th>Week</th>
                    <th>Start</th>
                    <th>Baseline In</th>
                    <th>Baseline Out</th>
                    <th>Reserved</th>
                    <th>Planned</th>
                    <th>Ending</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {state.capitalPlan.weeks.map((week) => (
                    <tr key={`capital-week-${week.week_index}`}>
                      <td>W{week.week_index + 1}</td>
                      <td>{week.week_start}</td>
                      <td>{formatMoney(week.baseline_inflows)}</td>
                      <td>{formatMoney(week.baseline_outflows)}</td>
                      <td>{formatMoney(week.reserved_outflows)}</td>
                      <td>{formatMoney(week.planned_outflows)}</td>
                      <td>{formatMoney(week.ending_balance)}</td>
                      <td>{week.notes || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DataTable>

            {[
              ["Funded Waves", state.capitalPlan.funded_waves],
              ["Deferred Waves", state.capitalPlan.deferred_waves],
              ["Reserved Waves", state.capitalPlan.reserved_waves],
            ].map(([label, waves]) => (
              <div key={label as string}>
                <h3>{label as string}</h3>
                <DataTable slim>
                  <table>
                    <thead>
                      <tr>
                        <th>Supplier</th>
                        <th>Wave</th>
                        <th>Cycle</th>
                        <th>Decision</th>
                        <th>Order Date</th>
                        <th>Immediate Cash</th>
                        <th>Protected Profit</th>
                        <th>Net Objective</th>
                        <th>Penalty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(waves as CapitalPlanWave[]).length > 0 ? (
                        (waves as CapitalPlanWave[]).map((wave) => (
                          <tr
                            key={wave.id}
                            className={wave.id === state.selectedCapitalWaveId ? "selected-row" : ""}
                            onClick={() => state.updateSelection({ capitalWaveId: wave.id })}
                          >
                            <td>{wave.supplier}</td>
                            <td>{wave.wave_index}</td>
                            <td>{wave.on_cycle ? "On-cycle" : "Off-cycle"}</td>
                            <td>
                              <div>{wave.decision_status}</div>
                              <div className="muted-small">{wave.decision_reason || "-"}</div>
                            </td>
                            <td>{formatDate(wave.proposal_order_date)}</td>
                            <td>{formatMoney(wave.immediate_cash_required)}</td>
                            <td>{formatMoney(wave.protected_profit_value)}</td>
                            <td>{formatMoney(wave.capital_efficiency.net_objective_value)}</td>
                            <td>{formatMoney(wave.capital_efficiency.capital_efficiency_penalty_value)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={9}>No waves in this bucket.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </DataTable>
              </div>
            ))}

            {state.selectedCapitalWave && (
              <>
                <h3>
                  Capital Wave: {state.selectedCapitalWave.supplier}{" "}
                  <span className="badge badge-neutral">{state.selectedCapitalWave.decision_status}</span>
                </h3>
                <div className="detail-cards">
                  <MetricCard label="Decision Reason" value={state.selectedCapitalWave.decision_reason || "-"} />
                  <MetricCard label="Order Date" value={formatDate(state.selectedCapitalWave.proposal_order_date)} />
                  <MetricCard label="Target Arrival" value={formatDate(state.selectedCapitalWave.target_arrival_date)} />
                  <MetricCard label="Total PO" value={formatMoney(state.selectedCapitalWave.total_order_value)} />
                  <MetricCard label="Immediate Cash" value={formatMoney(state.selectedCapitalWave.immediate_cash_required)} />
                  <MetricCard label="Protected Profit" value={formatMoney(state.selectedCapitalWave.protected_profit_value)} />
                  <MetricCard label="Net Objective" value={formatMoney(state.selectedCapitalWave.capital_efficiency.net_objective_value)} />
                  <MetricCard
                    label="Dominant Reason"
                    value={formatCapitalReason(state.selectedCapitalWave.capital_efficiency.dominant_reason)}
                  />
                  <MetricCard
                    label="Signal Coverage"
                    value={formatCapitalSignalCoverage(state.selectedCapitalWave.capital_efficiency.signal_coverage)}
                  />
                </div>

                {state.selectedCapitalWave.decision_status === "funded" && state.selectedCapitalWave.supplier_order_proposal_id && (
                  <div className="actions">
                    <button
                      type="button"
                      onClick={() => state.updateSelection({ proposalId: state.selectedCapitalWave?.supplier_order_proposal_id || null })}
                      disabled={state.isRunContextLoading}
                    >
                      Open Linked Supplier Proposal
                    </button>
                  </div>
                )}

                <DataTable slim>
                  <table>
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Tier</th>
                        <th>Requested</th>
                        <th>Planned</th>
                        <th>Value</th>
                        <th>Protected Profit</th>
                        <th>Net Objective</th>
                        <th>Penalty</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {state.selectedCapitalWave.lines.map((line) => (
                        <tr key={line.id}>
                          <td>{line.sku}</td>
                          <td>{line.tier}</td>
                          <td>{line.requested_qty_units}</td>
                          <td>{line.planned_qty_units}</td>
                          <td>{formatMoney(line.line_order_value)}</td>
                          <td>{formatMoney(line.protected_profit_value)}</td>
                          <td>{formatMoney(line.capital_efficiency.net_objective_value)}</td>
                          <td>{formatMoney(line.capital_efficiency.capital_efficiency_penalty_value)}</td>
                          <td>{line.selection_reason || (line.is_supporting_fill ? "supporting_fill" : "selected")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </DataTable>
              </>
            )}
          </>
        ) : (
          <EmptyState
            title="No buying plan yet"
            message="Refresh the buying plan to surface the next best approvals, supplier buys, and order opportunities."
            compact
            actionLabel="Refresh Buying Plan"
            onAction={() => void state.handleRunCapitalPlan()}
          />
        )}
      </Panel>

      <Panel>
        <h2>Approve & place orders</h2>
        {state.purchaseOrderWarning && <WarningPanel title="Order needs attention" warnings={[state.purchaseOrderWarning]} />}
        <form className="po-form" onSubmit={state.handleCreatePo}>
          <label>
            Supplier
            <input value={state.poSupplier} onChange={(event) => state.setPoSupplier(event.target.value)} required />
          </label>
          <label>
            SKU
            <input value={state.poSku} onChange={(event) => state.setPoSku(event.target.value)} required />
          </label>
          <label>
            Units
            <input type="number" step="1" value={state.poUnitsDraft} onChange={(event) => state.setPoUnitsDraft(event.target.value)} required />
          </label>
          <label>
            Unit Cost
            <input
              type="number"
              step="0.01"
              value={state.poUnitCostDraft}
              onChange={(event) => state.setPoUnitCostDraft(event.target.value)}
              required
            />
          </label>
          <label>
            Receipt Date
            <input type="date" value={state.poReceiptDate} onChange={(event) => state.setPoReceiptDate(event.target.value)} required />
          </label>
          <button type="submit" disabled={state.loading || !state.canCreatePo}>
            Create Order
          </button>
        </form>

        <div className="po-workspace">
          <div className="po-columns">
            <div>
              {state.isRunContextLoading && <p className="muted-small">Refreshing the latest buy recommendations...</p>}
              {[
                ["Ready to approve", state.groupedProposals.proposed],
                ["Approved", state.groupedProposals.approved],
                ["Converted to order", state.groupedProposals.converted],
                ["Rejected", state.groupedProposals.rejected],
              ].map(([label, proposals]) => (
                <div key={label as string} className="po-status-group">
                  <h3>{label as string}</h3>
                  <DataTable slim>
                    <table>
                      <thead>
                        <tr>
                          <th>Supplier</th>
                          <th>Wave</th>
                          <th>Cycle</th>
                          <th>Total PO</th>
                          <th>Cash Now</th>
                          <th>Protected Profit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(proposals as SupplierOrderProposal[]).map((proposal) => (
                          <tr
                            key={proposal.id}
                            className={proposal.id === state.selectedProposalId ? "selected-row" : ""}
                            onClick={() => {
                              if (state.isRunContextLoading) {
                                return;
                              }
                              state.updateSelection({ proposalId: proposal.id });
                            }}
                          >
                            <td>{proposal.supplier}</td>
                            <td>{proposal.proposal_order_date}</td>
                            <td>{proposal.on_cycle ? "On-cycle" : "Off-cycle"}</td>
                            <td>{formatMoney(proposal.total_order_value)}</td>
                            <td>{formatMoney(proposal.immediate_cash_required)}</td>
                            <td>{formatMoney(proposal.protected_profit_value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </DataTable>
                </div>
              ))}

              {[
                ["Approved Draft POs", ["approved", "planned"]],
                ["Placed / In Production", ["placed", "deposit_paid", "in_production"]],
                ["Shipped / Awaiting Receipt", ["shipped"]],
                ["Received", ["received"]],
              ].map(([label, statuses]) => (
                <div key={label as string} className="po-status-group">
                  <h3>{label as string}</h3>
                  <DataTable slim>
                    <table>
                      <thead>
                        <tr>
                          <th>Supplier</th>
                          <th>Status</th>
                          <th>Arrival</th>
                          <th>Committed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {state.purchaseOrders
                          .filter((po) => (statuses as string[]).includes(po.status))
                          .map((po) => (
                            <tr
                              key={po.id}
                              className={po.id === state.selectedPoId ? "selected-row" : ""}
                              onClick={() => state.updateSelection({ poId: po.id })}
                            >
                              <td>{po.supplier}</td>
                              <td>{po.status}</td>
                              <td>{po.expected_arrival_date || "-"}</td>
                              <td>{po.is_committed ? "Yes" : "No"}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </DataTable>
                </div>
              ))}
            </div>

            <div>
              {state.selectedProposal ? (
                <>
                  <h3>
                    Buy recommendation: {state.selectedProposal.supplier}{" "}
                    <span className="badge badge-neutral">{state.selectedProposal.proposal_status}</span>
                  </h3>
                  <div className="detail-cards">
                    <MetricCard label="Wave Date" value={formatDate(state.selectedProposal.proposal_order_date)} />
                    <MetricCard
                      label="Cycle"
                      value={
                        state.selectedProposal.on_cycle
                          ? `On-cycle${state.selectedProposal.cadence_bucket_date ? ` | bucket ${state.selectedProposal.cadence_bucket_date}` : ""}`
                          : "Off-cycle"
                      }
                    />
                    <MetricCard label="Target Arrival" value={formatDate(state.selectedProposal.target_arrival_date)} />
                    <MetricCard label="Total PO" value={formatMoney(state.selectedProposal.total_order_value)} />
                    <MetricCard label="Immediate Cash" value={formatMoney(state.selectedProposal.immediate_cash_required)} />
                    <MetricCard label="Protected Profit" value={formatMoney(state.selectedProposal.protected_profit_value)} />
                    <MetricCard
                      label="Freight Rule"
                      value={(state.selectedProposal.explainability?.freight_label as string | undefined) || "fallback"}
                    />
                    <MetricCard
                      label="Minimum Fill"
                      value={`${Number((state.selectedProposal.explainability?.min_order_fill_pct as number | undefined) || 100).toFixed(1)}%`}
                    />
                    <MetricCard label="Cash Feasibility" value={state.selectedProposal.cash_feasibility_status || "-"} />
                    <MetricCard label="Linked PO" value={state.relatedProposalPo ? state.relatedProposalPo.id : "Not converted"} />
                  </div>

                  <WarningPanel title="Things to review" warnings={state.proposalWarnings} />

                  {(state.selectedProposal.feedback_summary || selectedSupplierFeedback) && (
                    <>
                      <h4>Supplier Scorecard</h4>
                      <div className="detail-cards">
                        <MetricCard
                          label="Median Arrival Delay"
                          value={
                            (selectedSupplierFeedback?.median_arrival_delay_days ??
                              state.selectedProposal.feedback_summary?.supplier_rollup?.median_arrival_delay_days ??
                              "-") as string | number
                          }
                        />
                        <MetricCard
                          label="P90 Arrival Delay"
                          value={
                            (selectedSupplierFeedback?.p90_arrival_delay_days ??
                              state.selectedProposal.feedback_summary?.supplier_rollup?.p90_arrival_delay_days ??
                              "-") as string | number
                          }
                        />
                        <MetricCard
                          label="On-Time Orders"
                          value={
                            selectedSupplierFeedback?.order_on_time_rate !== null &&
                            selectedSupplierFeedback?.order_on_time_rate !== undefined
                              ? formatPct(selectedSupplierFeedback.order_on_time_rate)
                              : "-"
                          }
                        />
                        <MetricCard
                          label="A-SKU Early Stockouts"
                          value={selectedSupplierFeedback?.a_sku_early_stockout_count ?? "-"}
                        />
                      </div>
                    </>
                  )}

                  <div className="actions">
                    {state.selectedProposal.proposal_status === "proposed" && (
                      <button
                        type="button"
                        onClick={() => state.handleProposalAction("approve")}
                        disabled={state.loading || state.isRunContextLoading}
                      >
                        Approve Proposal
                      </button>
                    )}
                    {["proposed", "approved"].includes(state.selectedProposal.proposal_status) && (
                      <button
                        type="button"
                        onClick={() => state.handleProposalAction("reject")}
                        disabled={state.loading || state.isRunContextLoading}
                      >
                        Reject Proposal
                      </button>
                    )}
                    {state.selectedProposal.proposal_status === "approved" && (
                      <button
                        type="button"
                        onClick={() => state.handleProposalAction("convert-to-po")}
                        disabled={state.loading || state.isRunContextLoading}
                      >
                        Convert To Approved PO
                      </button>
                    )}
                    {state.relatedProposalPo && (
                      <button type="button" onClick={() => state.updateSelection({ poId: state.relatedProposalPo?.id || null })}>
                        Open Linked PO
                      </button>
                    )}
                  </div>

                  <h4>Selected Lines</h4>
                  <DataTable slim>
                    <table>
                      <thead>
                        <tr>
                          <th>SKU</th>
                          <th>Tier</th>
                          <th>Requested</th>
                          <th>Selected</th>
                          <th>Value</th>
                          <th>Protected Profit</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {state.selectedProposal.lines.map((line) => (
                          <tr key={line.id}>
                            <td>{line.sku}</td>
                            <td>{line.tier}</td>
                            <td>{line.requested_qty_units}</td>
                            <td>{line.selected_qty_units}</td>
                            <td>{formatMoney(line.line_order_value)}</td>
                            <td>{formatMoney(line.protected_profit_value)}</td>
                            <td>{line.selection_reason || (line.is_supporting_fill ? "supporting_fill" : "selected")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </DataTable>

                  <h4>Omitted Supplier Lines</h4>
                  <DataTable slim>
                    <table>
                      <thead>
                        <tr>
                          <th>SKU</th>
                          <th>Tier</th>
                          <th>Raw Qty</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {state.proposalOmittedRows.map((row) => (
                          <tr key={`omitted-${row.sku}`}>
                            <td>{row.sku}</td>
                            <td>{row.tier}</td>
                            <td>{row.rawQty}</td>
                            <td>{row.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </DataTable>

                  <h4>Supplier Settings</h4>
                  <DataTable slim>
                    <table>
                      <thead>
                        <tr>
                          <th>Setting</th>
                          <th>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Minimum Order Value</td>
                          <td>{formatMoney(state.supplierProfile?.min_order_value)}</td>
                        </tr>
                        <tr>
                          <td>Cadence</td>
                          <td>{state.supplierProfile?.cadence_days ? `${state.supplierProfile.cadence_days} days` : "none"}</td>
                        </tr>
                        <tr>
                          <td>Grouping Window</td>
                          <td>{state.supplierProfile?.grouping_window_days ?? "-"} days</td>
                        </tr>
                        <tr>
                          <td>Deposit</td>
                          <td>{state.proposalSupplierTerms?.deposit_pct ?? "-"}%</td>
                        </tr>
                      </tbody>
                    </table>
                  </DataTable>

                  <h4>Freight Breakpoints</h4>
                  <DataTable slim>
                    <table>
                      <thead>
                        <tr>
                          <th>Basis</th>
                          <th>Min</th>
                          <th>Max</th>
                          <th>Cost</th>
                          <th>Free</th>
                        </tr>
                      </thead>
                      <tbody>
                        {state.supplierFreightRules.map((rule) => (
                          <tr key={`freight-${rule.id}`}>
                            <td>{rule.rule_basis}</td>
                            <td>{rule.min_threshold}</td>
                            <td>{rule.max_threshold || "-"}</td>
                            <td>{formatMoney(rule.freight_cost)}</td>
                            <td>{rule.is_free ? "Yes" : "No"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </DataTable>
                </>
              ) : (
                <p>No supplier proposal selected.</p>
              )}

              {state.selectedPo && (
                <>
                  <h3 className="subsection-title">
                    PO Detail: {state.selectedPo.supplier} <span className="badge badge-neutral">{state.selectedPo.status}</span>
                  </h3>
                  <div className="detail-cards">
                    <MetricCard label="Recommendation Lineage" value={state.selectedPo.recommendation_run_id || "manual"} />
                    <MetricCard label="Supplier Proposal" value={state.selectedPo.supplier_order_proposal_id || "-"} />
                    <MetricCard label="Commitment" value={state.selectedPo.is_committed ? "Committed" : "Draft"} />
                    <MetricCard label="Physical ETA" value={state.selectedPo.target_physical_arrival_date || state.selectedPo.expected_arrival_date || "-"} />
                    <MetricCard label="Amazon ETA" value={state.selectedPo.target_amazon_received_date || "-"} />
                    <MetricCard
                      label="Sellable ETA"
                      value={state.selectedPo.target_sellable_available_date || state.selectedPo.expected_arrival_date || "-"}
                    />
                  </div>

                  {state.selectedPo.inbound_summary && (
                    <>
                      <h4>Inbound Reconciliation</h4>
                      <div className="detail-cards">
                        <MetricCard label="Committed" value={state.selectedPo.inbound_summary.stage_totals?.committed_units ?? "-"} />
                        <MetricCard label="In Transit" value={state.selectedPo.inbound_summary.stage_totals?.shipped_units ?? "-"} />
                        <MetricCard label="Amazon Received" value={state.selectedPo.inbound_summary.stage_totals?.amazon_received_units ?? "-"} />
                        <MetricCard label="Sellable" value={state.selectedPo.inbound_summary.stage_totals?.sellable_available_units ?? "-"} />
                        <MetricCard label="Exceptions" value={Number(state.selectedPo.inbound_summary.exception_summary?.active || 0)} />
                        <MetricCard label="Observed Truth" value={sumTruthStageUnits(state.selectedPo.inbound_summary.truth_summary?.observed_stage_units)} />
                        <MetricCard label="Inferred Truth" value={sumTruthStageUnits(state.selectedPo.inbound_summary.truth_summary?.inferred_stage_units)} />
                        <MetricCard label="Truth Conflicts" value={state.selectedPo.inbound_summary.truth_summary?.active_conflict_count ?? 0} />
                        <MetricCard label="Truth Source" value={state.selectedPo.inbound_summary.truth_summary?.dominant_source || "-"} />
                        <MetricCard label="Last Observed" value={formatDateTime(state.selectedPo.inbound_summary.truth_summary?.last_observed_at || null)} />
                      </div>
                    </>
                  )}

                  {(state.selectedPo.feedback_summary || selectedSupplierFeedback) && (
                    <>
                      <h4>PO Feedback Context</h4>
                      <div className="detail-cards">
                        <MetricCard
                          label="Arrival Status"
                          value={state.selectedPo.feedback_summary?.arrival_context?.arrival_status || "-"}
                        />
                        <MetricCard
                          label="Arrival Delay"
                          value={
                            (state.selectedPo.feedback_summary?.arrival_context?.arrival_delay_days ?? "-") as
                              | string
                              | number
                          }
                        />
                        <MetricCard label="Supplier Median Delay" value={selectedSupplierFeedback?.median_arrival_delay_days ?? "-"} />
                        <MetricCard label="Supplier Cash Warnings" value={selectedSupplierFeedback?.cash_warning_count ?? "-"} />
                      </div>
                    </>
                  )}

                  <div className="actions">
                    {state.selectedPo.status === "planned" && <button type="button" onClick={() => state.handlePoAction("approve")}>Approve</button>}
                    {state.selectedPo.status === "approved" && (
                      <button type="button" onClick={() => state.handlePoAction("place", { order_date: toLocalIsoDate() })}>
                        Place
                      </button>
                    )}
                    {state.selectedPo.status === "placed" && (
                      <button
                        type="button"
                        onClick={() => state.handlePoAction("mark-deposit-paid", { paid_date: toLocalIsoDate() })}
                      >
                        Deposit Paid
                      </button>
                    )}
                    {state.selectedPo.status === "deposit_paid" && (
                      <button type="button" onClick={() => state.handlePoAction("start-production")}>
                        Start Production
                      </button>
                    )}
                    {["placed", "deposit_paid", "in_production", "shipped"].includes(state.selectedPo.status) && (
                      <button type="button" onClick={() => state.handleAutoShip()}>
                        Add Shipment
                      </button>
                    )}
                    {state.selectedPo.status === "shipped" && (
                      <button type="button" onClick={() => state.handleAutoReceive()}>
                        Receive Remaining
                      </button>
                    )}
                    <button type="button" onClick={() => navigate(`/operations?poId=${state.selectedPo?.id}`)}>
                      Open In Operations
                    </button>
                  </div>

                  <h4>PO Timeline</h4>
                  <DataTable slim>
                    <table>
                      <thead>
                        <tr>
                          <th>When</th>
                          <th>Kind</th>
                          <th>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {state.selectedPoTimeline.map((event, index) => (
                          <tr key={`${event.kind}-${index}`}>
                            <td>{event.event_at ? formatDateTime(event.event_at) : "-"}</td>
                            <td>{event.kind}</td>
                            <td>{JSON.stringify(event.payload)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </DataTable>

                  <h4>PO Lines</h4>
                  <DataTable slim>
                    <table>
                      <thead>
                        <tr>
                          <th>SKU</th>
                          <th>Approved</th>
                          <th>Received</th>
                          <th>Status</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {state.selectedPo.lines.map((line) => (
                          <tr key={`po-line-${line.id}`}>
                            <td>{line.sku}</td>
                            <td>{line.approved_qty_units || line.units}</td>
                            <td>{line.received_qty_units || 0}</td>
                            <td>{line.line_status}</td>
                            <td>{line.line_notes || ""}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </DataTable>

                  <h4>PO Shipments</h4>
                  <DataTable slim>
                    <table>
                      <thead>
                        <tr>
                          <th>Ref</th>
                          <th>Amazon Plan</th>
                          <th>Amazon Shipment</th>
                          <th>Route</th>
                          <th>Status</th>
                          <th>Ship Date</th>
                          <th>Physical</th>
                          <th>Amazon</th>
                          <th>Sellable</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(state.selectedPo.shipments || []).map((shipment) => (
                          <tr key={`po-shipment-${shipment.id}`}>
                            <td>{shipment.shipment_ref}</td>
                            <td>{shipment.amazon_inbound_plan_id || "-"}</td>
                            <td>{shipment.amazon_shipment_id || "-"}</td>
                            <td>{shipment.route_type || "-"}</td>
                            <td>{shipment.status}</td>
                            <td>{shipment.ship_date || "-"}</td>
                            <td>{shipment.expected_physical_arrival_date || shipment.expected_arrival_date || shipment.actual_arrival_date || "-"}</td>
                            <td>{shipment.expected_amazon_received_date || "-"}</td>
                            <td>{shipment.expected_sellable_available_date || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </DataTable>

                  <h4>PO Receipts</h4>
                  <DataTable slim>
                    <table>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Stage</th>
                          <th>Source</th>
                          <th>Shipment</th>
                          <th>Confidence</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(state.selectedPo.receipts || []).map((receipt) => (
                          <tr key={`po-receipt-${receipt.id}`}>
                            <td>{receipt.receipt_date}</td>
                            <td>{receipt.receipt_stage || "-"}</td>
                            <td>{receipt.receipt_source || "-"}</td>
                            <td>{receipt.shipment_id || "-"}</td>
                            <td>{receipt.confidence || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </DataTable>

                  <h4>Inbound Exceptions</h4>
                  <DataTable slim>
                    <table>
                      <thead>
                        <tr>
                          <th>SKU</th>
                          <th>Type</th>
                          <th>Stage</th>
                          <th>Status</th>
                          <th>Blocked</th>
                          <th>Owner</th>
                          <th>Next Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(state.selectedPo.exceptions || []).map((exception) => (
                          <tr key={`po-exception-${exception.id}`}>
                            <td>{exception.sku}</td>
                            <td>{exception.exception_type}</td>
                            <td>{exception.stage}</td>
                            <td>{exception.status}</td>
                            <td>{exception.blocked_units || exception.units_affected}</td>
                            <td>{exception.assigned_to || exception.external_party || "-"}</td>
                            <td>{exception.next_action_date || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </DataTable>

                  <h4>PO Payments</h4>
                  <DataTable slim>
                    <table>
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Status</th>
                          <th>Due</th>
                          <th>Paid</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(state.selectedPo.payments || []).map((payment) => (
                          <tr key={`po-payment-${payment.id}`}>
                            <td>{payment.payment_type}</td>
                            <td>{payment.status}</td>
                            <td>{payment.due_date || "-"}</td>
                            <td>{payment.paid_date || "-"}</td>
                            <td>{formatMoney(payment.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </DataTable>
                </>
              )}
            </div>
          </div>
        </div>
      </Panel>

      {state.error && (
        <Panel className="warning-panel">
          <p>{state.error}</p>
        </Panel>
      )}
    </>
  );
}
